import { useMemo, useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@heroui/react'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection } from '@/components/ui/form-section'
import {
  DateFieldControl,
  NumberFieldControl,
  SelectFieldControl,
  TextAreaFieldControl,
} from '@/components/ui/form-fields'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import {
  useAdjustmentActions,
  useInventoryLocations,
  useReasonCodes,
  useStockItems,
  useStockLevels,
} from '../hooks'
import type { AdjustmentKind, CreateAdjustmentBody, StockItem, StockLevel } from '../types'
import {
  adjustmentKindHints,
  adjustmentKindLabels,
  inOutletOrder,
  isCounted,
  locationLabel,
  quantity,
  reasonCategoriesOf,
  reasonCategoryLabels,
  variance,
} from './inventory-format'

/** Quantities take four decimals, as the service stores them; a unit cost six. */
const QUANTITY_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 4 }
const COST_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 6 }

/** What can be adjusted: the first page of stock items, as large as the API allows. */
const CANDIDATE_QUERY = { limit: PAGE_LIMIT_MAX, offset: 0 }

/** The batch stock read answers for at most this many items. */
const STOCK_READ_MAX = 50

const KINDS = ['adjustment', 'wastage', 'opening'] as const satisfies readonly AdjustmentKind[]

const lineSchema = z.object({
  itemId: z.string().min(1, 'Choose an item.'),
  qty: z.number().optional(),
  reasonCodeId: z.string().optional(),
  unitCost: z.number().optional(),
})

/** The rules that need nothing but the form; the cost rule needs the items, see `submit`. */
const schema = z
  .object({
    kind: z.enum(KINDS),
    locationId: z.string().min(1, 'Choose a location.'),
    /** `yyyy-mm-dd`; empty or today means now. */
    occurredOn: z.string().optional(),
    note: z.string().max(2000).optional(),
    lines: z.array(lineSchema).min(1, 'Add at least one item.'),
  })
  .superRefine((values, context) => {
    values.lines.forEach((line, index) => {
      const at = (field: string, message: string) =>
        context.addIssue({ code: 'custom', path: ['lines', index, field], message })

      if (line.qty === undefined) at('qty', 'Enter a quantity.')
      else if (values.kind === 'adjustment' && line.qty === 0)
        at('qty', 'Enter a change other than 0.')
      else if (values.kind !== 'adjustment' && line.qty <= 0) at('qty', 'Enter more than 0.')

      if (values.kind !== 'opening' && !line.reasonCodeId) at('reasonCodeId', 'Choose a reason.')
      if (values.kind === 'opening' && line.unitCost === undefined) {
        at('unitCost', 'Enter what one unit cost.')
      }
    })

    const ids = values.lines.map((line) => line.itemId).filter(Boolean)
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: 'custom',
        path: ['lines'],
        message: 'Each item can only be listed once.',
      })
    }
  })

type FormValues = z.infer<typeof schema>

const NEW_LINE: FormValues['lines'][number] = { itemId: '' }

/** Today as the date field spells it, in the browser's own time zone. */
function today(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** An item with no cost yet: found stock of it needs one typed in, which becomes its cost. */
function hasNoCost(item: StockItem | undefined): boolean {
  return !!item && !(item.currentAvgCost > 0) && !(item.defaultUnitCost > 0)
}

/** Whether the line asks for a unit cost: always on an opening, on found stock of a costless item. */
function needsCost(kind: AdjustmentKind, item: StockItem | undefined, qty: number | undefined) {
  return kind === 'opening' || (kind === 'adjustment' && (qty ?? 0) > 0 && hasNoCost(item))
}

interface AdjustmentFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSaved: () => void
}

/**
 * A new adjustment, wastage or opening balance at one location, saved as a draft.
 *
 * Quantities are in each item's own unit. An adjustment's are signed — a minus
 * takes stock away; a wastage's are what was thrown out; an opening's what is on
 * the shelf on day one, with what one unit cost. Every line but an opening's
 * says why, from the reasons its kind allows.
 *
 * Saving moves no stock: the draft is submitted from the list.
 */
export function AdjustmentFormDialog({ isOpen, onOpenChange, onSaved }: AdjustmentFormDialogProps) {
  const { create } = useAdjustmentActions()

  /* One id for the document, however often the save is retried: the service
     answers a second copy of it with the first. */
  const [clientDocId] = useState(() => crypto.randomUUID())
  /* One idempotency key per distinct body, as on the other create forms. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const { control, handleSubmit, formState, setError } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      kind: 'adjustment',
      locationId: '',
      occurredOn: today(),
      note: '',
      lines: [NEW_LINE],
    },
  })

  const lines = useFieldArray({ control, name: 'lines' })
  const kind = useWatch({ control, name: 'kind' })
  const locationId = useWatch({ control, name: 'locationId' })
  const values = useWatch({ control, name: 'lines' })

  const { locations } = useInventoryLocations()
  const locationOptions = useMemo(
    () =>
      inOutletOrder(locations).map((location) => ({
        value: location.id,
        label: locationLabel(location),
      })),
    [locations],
  )

  /* Only counted items hold stock; the service refuses the others. */
  const { items: candidates } = useStockItems(CANDIDATE_QUERY)
  const items = useMemo(() => candidates.filter(isCounted), [candidates])
  const itemOptions = useMemo(
    () => items.map((item) => ({ value: item.id, label: `${item.name} (${item.unitOfMeasure})` })),
    [items],
  )

  /* The reasons a picker offers: active, not system, in the kind's categories. */
  const { reasons } = useReasonCodes()
  const reasonOptions = useMemo(
    () =>
      reasons
        .filter(
          (reason) =>
            !reason.system &&
            reason.status === 'active' &&
            reasonCategoriesOf[kind].includes(reason.category),
        )
        .map((reason) => ({
          value: reason.id,
          label: `${reason.name} · ${reasonCategoryLabels[reason.category]}`,
        })),
    [reasons, kind],
  )

  /* What each chosen item holds at the location now, so a line shows what it
     will change before it is saved. */
  const chosenIds = useMemo(
    () => [...new Set(values.map((line) => line.itemId).filter(Boolean))].slice(0, STOCK_READ_MAX),
    [values],
  )
  const { levels } = useStockLevels(chosenIds, locationId || null)

  const submit = handleSubmit(async (form) => {
    // the one rule the schema cannot see: found stock of an item with no cost yet
    let missingCost = false
    form.lines.forEach((line, index) => {
      const item = items.find((candidate) => candidate.id === line.itemId)
      if (needsCost(form.kind, item, line.qty) && line.unitCost === undefined) {
        setError(`lines.${index}.unitCost`, {
          message: 'This item has no cost yet: enter what one unit cost.',
        })
        missingCost = true
      }
    })
    if (missingCost) return

    const body: CreateAdjustmentBody = {
      kind: form.kind,
      locationId: form.locationId,
      clientDocId,
      // a day in the past is taken at noon; today is now
      ...(form.occurredOn &&
        form.occurredOn !== today() && {
          occurredAt: new Date(`${form.occurredOn}T12:00:00`).toISOString(),
        }),
      ...(form.note?.trim() && { note: form.note.trim() }),
      lines: form.lines.map((line) => {
        const item = items.find((candidate) => candidate.id === line.itemId)
        return {
          itemId: line.itemId,
          qty: line.qty as number,
          ...(form.kind !== 'opening' && { reasonCodeId: line.reasonCodeId }),
          ...(needsCost(form.kind, item, line.qty) && { unitCost: line.unitCost }),
        }
      }),
    }

    const serialised = JSON.stringify(body)
    const next =
      attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
    setAttempt(next)

    await create.mutateAsync({ body, idempotencyKey: next.key })
    onSaved()
  })

  const listError = formState.errors.lines?.root?.message ?? formState.errors.lines?.message

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="New adjustment"
      description="Saved as a draft. Stock only changes once it is submitted (and approved, over your threshold)."
      submitLabel="Save draft"
      onSubmit={submit}
      isBusy={create.isPending}
      error={create.error}
      errorFallback="Could not save this adjustment."
      dialogClassName="max-w-4xl"
      scroll="inside"
    >
      <FormSection title="The adjustment" description={adjustmentKindHints[kind]}>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectFieldControl
            control={control}
            name="kind"
            label="Kind"
            isRequired
            options={KINDS.map((value) => ({ value, label: adjustmentKindLabels[value] }))}
          />
          <SelectFieldControl
            control={control}
            name="locationId"
            label="Location"
            placeholder="Choose…"
            isRequired
            options={locationOptions}
          />
          <DateFieldControl
            control={control}
            name="occurredOn"
            label="Happened on"
            max={today()}
            description="An earlier day is recorded at noon."
          />
        </div>
        <TextAreaFieldControl control={control} name="note" label="Note" placeholder="Optional" />
      </FormSection>

      <FormSection
        title="Items"
        description={
          kind === 'adjustment'
            ? 'How much each item goes up or down by, in its own unit — a minus takes stock away.'
            : kind === 'wastage'
              ? 'How much of each item was thrown out or used, in its own unit.'
              : 'What is on the shelf, in each item’s own unit, and what one unit cost. Only for items with no stock history at this location.'
        }
        action={
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-8 rounded-full border px-3 text-xs"
            onPress={() => lines.append(NEW_LINE)}
          >
            <PlusIcon width={14} height={14} />
            Add item
          </Button>
        }
        alert={
          listError && (
            <p role="alert" className="text-danger mt-2 text-xs">
              {listError}
            </p>
          )
        }
      >
        <div className="flex flex-col gap-3">
          {lines.fields.map((field, index) => {
            const line = values[index]
            const item = items.find((candidate) => candidate.id === line?.itemId)
            const showCost = needsCost(kind, item, line?.qty)

            return (
              <div
                key={field.id}
                className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1fr)_150px_minmax(0,1fr)_auto]"
              >
                <SelectFieldControl
                  control={control}
                  name={`lines.${index}.itemId` as const}
                  label="Item"
                  placeholder="Choose…"
                  isRequired
                  options={itemOptions}
                />
                <NumberFieldControl
                  control={control}
                  name={`lines.${index}.qty` as const}
                  label={`${kind === 'adjustment' ? 'Change' : 'Quantity'}${item ? ` (${item.unitOfMeasure})` : ''}`}
                  minValue={kind === 'adjustment' ? undefined : 0}
                  formatOptions={QUANTITY_FORMAT}
                  isRequired
                />
                {kind === 'opening' ? (
                  <span aria-hidden className="hidden sm:block" />
                ) : (
                  <SelectFieldControl
                    control={control}
                    name={`lines.${index}.reasonCodeId` as const}
                    label="Reason"
                    placeholder="Choose…"
                    isRequired
                    options={reasonOptions}
                  />
                )}
                <Button
                  variant="ghost"
                  aria-label="Remove item"
                  isDisabled={lines.fields.length === 1}
                  onPress={() => lines.remove(index)}
                  className="text-muted hover:text-danger size-11 self-end rounded-full"
                >
                  <TrashIcon width={16} height={16} />
                </Button>

                {showCost && (
                  <div className="sm:col-span-2">
                    <NumberFieldControl
                      control={control}
                      name={`lines.${index}.unitCost` as const}
                      label={`Cost of one ${item?.unitOfMeasure ?? 'unit'}`}
                      minValue={0}
                      formatOptions={COST_FORMAT}
                      isRequired
                      description={
                        kind === 'opening'
                          ? 'Becomes the item’s average cost.'
                          : 'The item has no cost yet; this becomes its cost.'
                      }
                    />
                  </div>
                )}

                {item && locationId && (
                  <p className="text-muted text-xs tabular-nums sm:col-span-4">
                    {onHandLine(levels.get(item.id), kind, line?.qty, item.unitOfMeasure)}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </FormSection>
    </FormModal>
  )
}

/**
 * `On hand here: 10 kg · after: 7 kg (−3)`. Only a preview: the service applies
 * the change to what is on hand when the document posts.
 */
function onHandLine(
  level: StockLevel | undefined,
  kind: AdjustmentKind,
  qty: number | undefined,
  unit: string,
): string {
  if (!level) return 'On hand here: …'

  const now = `On hand here: ${quantity(level.onHand)} ${unit}`
  if (qty === undefined) return now

  const change = kind === 'wastage' ? -qty : qty
  const after = level.onHand + change

  return `${now} · after: ${quantity(after)} ${unit} (${variance(change)})`
}
